
import { Component, Vue, Watch } from 'vue-property-decorator'
import groupBy from 'lodash/groupBy'
import Cookies from 'js-cookie'
import { Route } from 'vue-router/types/router'
import {
  actionResolver,
  EventFireData,
  GTM_EVENT_FIRE_EVENT_NAME,
  GTM_MAIN_EVENT_NAME,
  GTM_PAGEVIEW_EVENT_NAME,
  GtmChannelFlag,
  GtmEvent,
  GtmEventNames,
  GtmEventTypes,
  gtmPushEventFire,
  ProductOptions,
} from '~/utils/tracking/gtm'
import {
  BannerClickEvent,
  BannerImpressionEvent,
  exportGtmBanner,
  GtmBanner,
} from '~/utils/tracking/gtm/banners'
import { BaseBannerData } from '~/utils/tracking/banners'
import { AddToCartEvent, RemoveFromCartEvent } from '~/utils/tracking/gtm/cart'
import {
  BaseProductDataArticles,
  exportGtmProduct,
  GtmProduct,
  ProductClickEvent,
  ProductDetailEvent,
  ProductImpressionEvent,
  ProductVariantGtm,
} from '~/utils/tracking/gtm/products'
import { GET_PRODUCTS_VARIANT_BY_IDS } from '~/graphql/gql/product'
import { GQLProduct } from '~/graphql/__generated__/interfaces'
import {
  createPageViewListener,
  PageViewEvent,
  PageViewLogger,
} from '~/utils/tracking/gtm/pageViews'
import { GQLLocalAccount } from '~/graphql/__generated__/interfacesLocal'

const PRODUCT_VISIBILITY_QUEUE_INTERVAL = 750
const BANNER_VISIBILITY_QUEUE_DELAY = 245
const COOKIE_NAME_GTM_INSPECT = 'GTM_INSPECT'

interface ErrorDescription {
  msg: string
  event?: GtmEvent
  prevError?: Error
}

@Component
export default class TheTrackingGtm extends Vue {
  account: GQLLocalAccount
  productVisibilityQueueIntervalId: number
  productVisibilityQueue: Record<string, ProductOptions>
  bannerVisibilityQueue: GtmBanner[]
  logPageView: PageViewLogger
  unregisterPageView: Function
  eventListener: Function
  eventFireListener: Function
  countVisiblePage = 0

  // because we don't want to reactivity in GTM data
  beforeCreate() {
    this.productVisibilityQueue = {}
    this.bannerVisibilityQueue = []
  }

  render() {
    return null
  }

  get clientWatchRouter() {
    if (!process.client) {
      return null
    }
    return this.$route
  }

  @Watch('clientWatchRouter', { immediate: true })
  handler() {
    this.countVisiblePage++
  }

  created() {
    if (!process.client) {
      return
    }

    this.productVisibilityQueueIntervalId = window.setInterval(
      this.processProductVisibilityQueue,
      PRODUCT_VISIBILITY_QUEUE_INTERVAL
    )

    window.addEventListener('beforeunload', this.processBannerVisibilityQueue)

    this.eventListener = async (evt: GtmEvent) => {
      try {
        const action = actionResolver({
          bannerDataResolver: this.resolveBannerData,
          pageType: this.pageType,
          bannerVisibilityQueue: this.bannerVisibilityQueue,
          countVisiblePage: this.countVisiblePage,
          evt,
          gtm: this.$gtm,
          productDataResolver: this.resolveProductData,
          productVisibilityQueue: this.productVisibilityQueue,
          inspectElement: this.inspectElement,
          pageTypeByRouteName: this.pageTypeByRouteName,
        })
        await action(evt)
      } catch (error) {}
    }
    window.addEventListener(
      GTM_MAIN_EVENT_NAME,
      this.eventListener as EventListener
    )

    this.logPageView = createPageViewListener(
      this.$shopConfig,
      this.$resize,
      this.$gtm,
      this.$pageLoad
    )

    const initialPageViewListener = (evt: PageViewEvent) => {
      const {
        detail: { traffic },
      } = evt

      this.logPageView(this.$route, traffic, { isInitial: true })
    }

    window.addEventListener(
      GTM_PAGEVIEW_EVENT_NAME,
      initialPageViewListener as EventListener,
      { once: true }
    )

    this.unregisterPageView = this.$router.afterEach((to, from) => {
      setTimeout(
        this.processBannerVisibilityQueue,
        BANNER_VISIBILITY_QUEUE_DELAY
      )
      const eventListener = (evt: PageViewEvent) => {
        const { detail } = evt
        const { traffic, sendChangeQuery } = detail

        const changeTerm = this.isChangeQuery(to, from, sendChangeQuery)

        if (
          changeTerm ||
          (to.path === detail.to.path && to.path !== from.path)
        ) {
          this.logPageView(to, traffic)
        }
      }
      window.addEventListener(
        GTM_PAGEVIEW_EVENT_NAME,
        eventListener as EventListener,
        { once: true }
      )
    })

    this.eventFireListener = (evt: CustomEvent<EventFireData>) => {
      gtmPushEventFire(this.$gtm, evt.detail)
    }
    window.addEventListener(
      GTM_EVENT_FIRE_EVENT_NAME,
      this.eventFireListener as EventListener
    )
  }

  isChangeQuery(to: Route, from: Route, queryTerm?: string | null) {
    if (!queryTerm) {
      return false
    }

    return to.query[queryTerm] !== from.query[queryTerm]
  }

  get gtmSsrEnabled() {
    return true
  }

  beforeDestroy() {
    window.clearInterval(this.productVisibilityQueueIntervalId)
    window.removeEventListener(
      'beforeunload',
      this.processBannerVisibilityQueue
    )
    window.removeEventListener(
      GTM_MAIN_EVENT_NAME,
      this.eventListener as EventListener
    )
    this.unregisterPageView()
    window.removeEventListener(
      GTM_EVENT_FIRE_EVENT_NAME,
      this.eventFireListener as EventListener
    )
  }

  private async processProductVisibilityQueue() {
    try {
      if (Object.keys(this.productVisibilityQueue).length > 0) {
        const queue = { ...this.productVisibilityQueue }
        this.productVisibilityQueue = {}

        const queueArray = Object.values(queue)

        const pageTypeList = queueArray[0].pageTypeList

        if (!pageTypeList) {
          this.$logger.error(
            'TheTrackingGtm.processProductVisibilityQueue - GA pageTypeList is undefined'
          )
        }

        const existOtherPageTypeInQueue = queueArray.find(
          (queueArray) => queueArray.pageTypeList !== pageTypeList
        )

        if (existOtherPageTypeInQueue) {
          this.$logger.error(
            'TheTrackingGtm.processProductVisibilityQueue - GA pageTypeList is exist other pageType in query'
          )
        }

        const impressions = await this.resolveProductsBatch(queue)
        this.$gtm.push({
          pageTypeList,
          ecommerce: { impressions },
          event: GtmEventNames.PRODUCT_IMPRESS,
        })
      }
    } catch (e) {
      this.$logger.error(
        `TheTrackingGtm.processProductVisibilityQueue - GA push error : ${e}`,
        e
      )
    }
  }

  private processBannerVisibilityQueue() {
    if (this.bannerVisibilityQueue.length > 0) {
      const groupedBanners = groupBy(this.bannerVisibilityQueue, 'bannerId')
      const groupedPromotions = Object.values(groupedBanners).map(
        (promotions: any) =>
          promotions.reduce((accumulator: any, promotion: any) => {
            const { bannerId, ...bannerData } = promotion

            return [...accumulator, bannerData]
          }, [] as Omit<GtmBanner, 'bannerId'>[])
      )
      Object.values(groupedPromotions).forEach((promotions) =>
        this.$gtm.push({
          ecommerce: {
            promoView: {
              promotions,
            },
          },
          event: GtmEventNames.INT_PROMO,
        })
      )
      this.bannerVisibilityQueue.length = 0
    }
  }

  private resolveBannerData(
    evt: BannerClickEvent | BannerImpressionEvent
  ): GtmBanner {
    return exportGtmBanner(evt.detail.data as BaseBannerData)
  }

  private createEventError({ msg, event, prevError }: ErrorDescription): Error {
    const error: any = new Error(`TheTrackingGtm: Event is not valid, ${msg}`)
    error.name = 'TheTrackingGtmError'
    error.path = this.$route.path
    if (event) {
      error.event = event
    }

    if (prevError) {
      error.prevError = prevError
    }
    return error
  }

  private isTheTrackingGtmError(error: Error) {
    return error?.name === 'TheTrackingGtmError'
  }

  private validateEventId(id: string) {
    const checkIsNumber = /^-?\d+$/.test(id)

    if (!checkIsNumber) {
      this.$logger.error(
        'TheTrackingGtm.validateEventId - GTM ID was not provided'
      )
      throw this.createEventError({
        msg: 'GTM ID was not provided',
      })
    }
  }

  private async resolveProductData(
    evt:
      | AddToCartEvent
      | RemoveFromCartEvent
      | ProductClickEvent
      | ProductDetailEvent
  ): Promise<GtmProduct[]> {
    const { articles } = evt.detail.data

    const articlesId = articles.map((article) => article.id)

    const response = (await this.$apollo.query({
      query: GET_PRODUCTS_VARIANT_BY_IDS,
      fetchPolicy: 'no-cache',
      variables: { ids: articlesId, withBundles: true, withGifts: true },
    })) as { data: { products: GQLProduct[] } }

    return articles.map((data) => {
      const {
        id,
        position,
        list,
        quantity,
        context,
        selectedBundles,
        bonusSetId,
      } = data

      this.validateEventId(id)

      try {
        const bundles =
          evt.detail.eventType === GtmEventTypes.AddToCart
            ? selectedBundles
            : undefined

        const product = this.findVariant(response.data.products, id)

        return exportGtmProduct(product, {
          context,
          eventType: evt.detail.eventType,
          list,
          position,
          quantity,
          bonusSetId,
          selectedBundles: bundles,
        })
      } catch (err) {
        if (this.isTheTrackingGtmError(err)) {
          throw err
        }

        throw this.createEventError({
          msg: 'resolveProductData failed',
          prevError: err,
          event: evt,
        })
      }
    })
  }

  private async resolveProductsBatch(
    queue: Record<string, ProductOptions>
  ): Promise<GtmProduct[]> {
    try {
      const ids = Object.keys(queue) ?? []

      ids.forEach((id) => {
        this.validateEventId(id)
      })

      const response = (await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_PRODUCTS_VARIANT_BY_IDS,
        variables: { ids },
      })) as { data: { products: GQLProduct[] } }

      const products = ids
        .map((id) => {
          return this.findVariant(response.data.products, id)
        })
        .filter((id) => id)

      return products.map((product) => {
        return exportGtmProduct(product, queue[product.variant.id])
      })
    } catch (err) {
      throw this.createEventError({
        msg: 'resolveProductsBatch was not finishned',
        prevError: err,
      })
    }
  }

  findVariant(products: GQLProduct[], id: string) {
    return products
      .map((product: GQLProduct) => {
        const mainVariant = product.variants.find(
          (variant) => variant.id === id
        )

        if (!mainVariant) {
          return null
        }

        return {
          ...product,
          variant: mainVariant,
        }
      })
      .find((product: ProductVariantGtm | null) => product) as ProductVariantGtm
  }

  pageTypeByRouteName(routeName: BaseProductDataArticles['routeName']) {
    const routeNamePageIdMapper: Record<string, GtmChannelFlag> = {
      campaign: GtmChannelFlag.CAMPAIGN,
      category: GtmChannelFlag.CATEGORY,
      detail: GtmChannelFlag.DETAIL,
      homepage: GtmChannelFlag.HOMEPAGE,
      search: GtmChannelFlag.SEARCH,
      partner: GtmChannelFlag.PARTNER,
      brand: GtmChannelFlag.BRAND,
      author: GtmChannelFlag.AUTHOR,
      licence: GtmChannelFlag.LICENCE,
    }

    const pageType = routeNamePageIdMapper[routeName as GtmChannelFlag]

    return pageType || routeName
  }

  get pageType(): GtmChannelFlag {
    const name = this.$route.name as GtmChannelFlag

    return this.pageTypeByRouteName(name)
  }

  inspectElement(
    evt: BannerImpressionEvent | ProductImpressionEvent,
    inspectData: object
  ) {
    if (!evt.target || Cookies.get(COOKIE_NAME_GTM_INSPECT) !== '1') {
      return
    }

    console.log('GTM-inspect', evt.target, JSON.stringify(inspectData))

    const inspectComponent = {
      template: `
        <div class="ga-event" style="position: relative">
        {{ inspectData }}
        </div>`,
      data: () => {
        return {
          inspectData,
        }
      },
    }

    const VueComp = Vue.extend(inspectComponent)

    const component = new VueComp().$mount()
    const evtTarget = evt.target as HTMLSelectElement

    evtTarget.append(component.$el)
  }
}
